// src/components/sidebar.jsx
// Fully merged with Merchant Admin access for Super Admins

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  doc, collection, query, orderBy, limit, getDoc,
  onSnapshot, deleteDoc, addDoc, serverTimestamp
} from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionPage from './SubscriptionPage';
import StyleTryon from './StyleTryon';

const GOLD = '#B8860B';
const DARK_NAVY = '#050a0f';
const SUPER_ADMINS = ["FFXDokpj5IXR3SFSvJyDu70LRp73", "GbjKMdeVEmTgMefcU1cEHsEOstn1"];

export default function Sidebar() {
  const [role, setRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastText, setBroadcastText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [systemAlert, setSystemAlert] = useState(null);
  const [alertVisible, setAlertVisible] = useState(false);

  // NEW: Style Try-On modal state
  const [showStyleTryon, setShowStyleTryon] = useState(false);
  // NEW: inline subscription plan selector
  const [showSubModal, setShowSubModal] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const sub = useSubscription();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const superStatus = SUPER_ADMINS.includes(user.uid);
        setIsSuperAdmin(superStatus);

        const [uSnap, bSnap] = await Promise.all([
          getDoc(doc(db, 'users', user.uid)),
          getDoc(doc(db, 'barbers', user.uid)),
        ]);

        let data = null;
        let detectedRole = 'client';

        if (uSnap.exists()) {
          data = uSnap.data();
          detectedRole = data.role || 'client';
        } else if (bSnap.exists()) {
          data = bSnap.data();
          detectedRole = 'stylist';
        }

        if (data) {
          setUserData(data);
          setRole(detectedRole);
          initAlertListener(detectedRole);
          initNotifListener(user.uid);
        }
      } else {
        setRole(null);
        setUserData(null);
        setIsSuperAdmin(false);
      }
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      unsubAuth();
    };
  }, []);

  const initAlertListener = (userRole) => {
    const q = query(collection(db, 'system_alerts'), orderBy('createdAt', 'desc'), limit(1));
    return onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const data = snap.docs[0].data();
        const targeted = data.target === 'all' ||
          (data.target === 'stylists' && userRole === 'stylist') ||
          (data.target === 'clients' && userRole === 'client');
        if (targeted) {
          setSystemAlert(data);
          setAlertVisible(true);
          setTimeout(() => setAlertVisible(false), 10000);
        }
      }
    });
  };

  const initNotifListener = (uid) => {
    const q = query(collection(db, 'users', uid, 'notifications'), orderBy('timestamp', 'desc'), limit(20));
    return onSnapshot(q, (snap) => {
      const notifs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    });
  };

  const handleBroadcastRequest = async () => {
    if (!broadcastText.trim()) return toast.error('Please enter a message');
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'broadcast_requests'), {
        stylistId: auth.currentUser.uid,
        stylistName: userData?.displayName || 'Unknown Stylist',
        message: broadcastText,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      toast.success('Request sent to Admin!');
      setBroadcastText('');
      setShowBroadcast(false);
    } catch {
      toast.error('Failed to send request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
    toast.success('SESSION TERMINATED');
  };

  const clearNotif = async (id) => {
    await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'notifications', id));
  };

  if (!role) return null;

  const links = {
    client: [
      { name: 'Dashboard', path: '/dashboard', icon: '🏠' },
      { name: '3D Scan', path: '/scan', icon: '👤' },
      { name: 'Biometric Health', path: '/biometric-health', icon: '🧬' },
      { name: 'Style Try-On', path: '#', icon: '🪄', action: () => setShowStyleTryon(true) },
      { name: '3D Viewer', path: '/viewer', icon: '👓' },
      { name: 'Consultations', path: '/consultation-history', icon: '📜' },
      { name: 'Global Vault', path: '/gallery', icon: '🌍' },
      { name: 'Wallet', path: '/wallet', icon: '💰' },
      { name: 'Get Help', path: '/help', icon: '🎧' },
    ],
    stylist: [
      { id: 'hub', name: 'Stylist Hub', path: '/stylist/dashboard', icon: '🛠️' },
      { id: '3d', name: '3D Projections', path: '/viewer', icon: '👓' },
      { id: 'workstation', name: 'Workstation', path: '/stylist/workstation', icon: '💻' },
      { id: 'tryon', name: 'Style Try-On', path: '#', icon: '🪄', action: () => setShowStyleTryon(true) },
      { id: 'history', name: 'Client Vaults', path: '/consultation-history', icon: '🗄️' },
      { id: 'revenue', name: 'Revenue', path: '/stylist/finances', icon: '📈' },
      { id: 'broadcast', name: 'Broadcast', path: '#', icon: '📢' },
    ],
    admin: [
      { name: 'Command Center', path: '/admin', icon: '🛡️' },
      { name: 'Financial Ledger', path: '/admin/dashboard', icon: '📊' },
      { name: 'Merchant Admin', path: '/admin/merchant', icon: '🏪' },   // ← Added
      { name: 'Support Hub', path: '/admin/support', icon: '🎫' },
      { name: 'Asset Vault', path: '/admin/vault', icon: '🔒' },
    ],
  };

  const activeLinks = isSuperAdmin ? links.admin : role === 'stylist' ? links.stylist : links.client;

  // Subscription badge label + action
  const subLabel = sub.isSuperAdmin
    ? '⚡ ROOT ACCESS'
    : sub.isSubscriptionActive
      ? `🛡️ SHIELD ACTIVE · ${sub.tier?.toUpperCase()}`
      : '🔓 UPGRADE TO SHIELD';

  const subSub = sub.isSuperAdmin
    ? 'Unlimited access'
    : sub.isSubscriptionActive
      ? `${sub.monthlyScansUsed}/${sub.PRICING[sub.tier]?.scansPerMonth} scans this month`
      : 'From ₦3,000/month';

  return (
    <>
      {/* SYSTEM ALERT BANNER */}
      {alertVisible && systemAlert && (
        <div style={toastContainer}>
          <div style={toastContent}>
            <div style={toastIcon}>⚠️</div>
            <div style={{ flex: 1 }}>
              <span style={toastLabel}>SYSTEM NOTICE</span>
              <p style={toastMsg}>{systemAlert.message}</p>
            </div>
            <button onClick={() => setAlertVisible(false)} style={toastClose}>✕</button>
          </div>
          <div style={toastProgress} />
        </div>
      )}

      {/* MOBILE TOGGLE */}
      {isMobile && (
        <button onClick={() => setIsOpen(!isOpen)} style={mobileToggle}>
          {isOpen ? '✕' : '☰'}
        </button>
      )}

      {/* STYLE TRY-ON MODAL */}
      {showStyleTryon && (
        <StyleTryon onClose={() => setShowStyleTryon(false)} />
      )}

      {/* BROADCAST MODAL (stylist only) */}
      {showBroadcast && (
        <div style={modalOverlayFixed}>
          <div style={broadcastBox}>
            <h3 style={{ color: GOLD, marginTop: 0, letterSpacing: '2px' }}>PREMIUM BROADCAST</h3>
            <p style={{ color: '#aaa', fontSize: '0.7rem', marginBottom: '15px' }}>
              Request a high-priority push notification to all clients.
            </p>
            <textarea
              placeholder="Enter broadcast message..."
              style={darkTextarea}
              value={broadcastText}
              onChange={e => setBroadcastText(e.target.value)}
            />
            <button disabled={isSubmitting} onClick={handleBroadcastRequest} style={goldBtn}>
              {isSubmitting ? 'TRANSMITTING...' : 'REQUEST BROADCAST'}
            </button>
            <button onClick={() => setShowBroadcast(false)} style={cancelLink}>ABORT</button>
          </div>
        </div>
      )}

      {/* SUBSCRIPTION PLAN MODAL */}
      {showSubModal && (
        <SubscriptionPage
          paywallMode={true}
          paywallReason={sub.isSubscriptionActive ? null : 'free_exhausted'}
          onClose={() => setShowSubModal(false)}
          redirectAfter="/dashboard"
        />
      )}

      {/* SIDEBAR */}
      <div style={{ ...sidebarContainer, left: isOpen ? '0' : '-280px' }}>
        {/* HEADER */}
        <div style={header}>
          <div style={logoSection}>
            <h1 style={logoText}>ESDRAS</h1>
            <span style={tagline}>MASTER CORE ENGINE</span>
          </div>
          <button onClick={() => setShowNotifPanel(!showNotifPanel)} style={notifBtn}>
            🔔 {unreadCount > 0 && <span style={notifBadge}>{unreadCount}</span>}
          </button>
        </div>

        {/* NOTIFICATION PANEL */}
        {showNotifPanel && (
          <div style={notifPanel}>
            <div style={notifHeader}>
              ACTIVE NOTIFICATIONS
              <button onClick={() => setShowNotifPanel(false)} style={{ background: 'none', border: 'none', color: GOLD, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={notifList}>
              {notifications.length === 0
                ? <p style={{ color: '#444', fontSize: '0.7rem', textAlign: 'center' }}>No notifications</p>
                : notifications.map(n => (
                  <div key={n.id} style={notifCard}>
                    <p style={{ margin: '0 0 6px', fontSize: '0.8rem', color: n.read ? '#555' : '#fff' }}>
                      {n.message || n.title}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.6rem', color: '#333' }}>
                        {n.timestamp?.toDate?.().toLocaleDateString() || 'Recent'}
                      </span>
                      <button onClick={() => clearNotif(n.id)} style={{ background: 'none', border: 'none', color: '#444', fontSize: '0.65rem', cursor: 'pointer' }}>
                        CLEAR
                      </button>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* SUBSCRIPTION BADGE */}
        <div style={{ padding: '0 15px 15px' }}>
          <div
            onClick={() => setShowSubModal(true)}
            style={{
              padding: '14px 16px',
              borderRadius: '14px',
              background: sub.isSubscriptionActive
                ? 'linear-gradient(135deg, #001F3F 0%, #050a0f 100%)'
                : 'rgba(255,255,255,0.03)',
              border: `1px solid ${sub.isSubscriptionActive ? GOLD : 'rgba(255,255,255,0.1)'}`,
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: 0, fontSize: '0.65rem', fontWeight: '900', color: GOLD, letterSpacing: '1px' }}>
              {subLabel}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '0.55rem', opacity: 0.6, color: 'white' }}>
              {subSub}
            </p>
          </div>
        </div>

        {/* NAV LINKS */}
        <nav style={navLinks}>
          {activeLinks.map((link) => {
            const isActive = location.pathname === link.path;
            const handleClick = link.action
              ? (e) => { e.preventDefault(); link.action(); }
              : link.id === 'broadcast'
                ? (e) => { e.preventDefault(); setShowBroadcast(true); }
                : undefined;

            return (
              <Link
                key={link.id || link.path}
                to={link.path}
                onClick={handleClick}
                style={{
                  ...navLink,
                  background: isActive ? 'rgba(184,134,11,0.12)' : 'transparent',
                  borderLeft: isActive ? `3px solid ${GOLD}` : '3px solid transparent',
                  color: isActive ? GOLD : '#ffffffb3',
                }}
              >
                <span style={iconSpan}>{link.icon}</span>
                {link.name}
                {link.name === 'Style Try-On' && !sub.canStyleTryOn.allowed && (
                  <span style={{ marginLeft: 'auto', fontSize: '0.55rem', color: '#ff4d4d' }}>🔒</span>
                )}
              </Link>
            );
          })}

          {/* Super admin root access */}
          {isSuperAdmin && location.pathname !== '/admin' && (
            <Link to="/admin" style={{ ...navLink, marginTop: '20px', border: `1px dashed ${GOLD}44`, color: GOLD }}>
              <span style={iconSpan}>🛡️</span> ROOT ACCESS
            </Link>
          )}
        </nav>

        {/* FOOTER */}
        <div style={footer}>
          <div style={userBrief}>
            <div style={avatar}>{userData?.displayName?.charAt(0) || 'U'}</div>
            <div style={userInfo}>
              <div style={userName}>{userData?.displayName || 'Authorized User'}</div>
              <div style={userRole}>{role?.toUpperCase()}</div>
            </div>
          </div>
          <button onClick={handleLogout} style={logoutBtn}>TERMINATE SESSION</button>
        </div>
      </div>
    </>
  );
}

// ── Styles (unchanged) ───────────────────────────────────────────────────────
const sidebarContainer = { width: '260px', height: '100vh', background: DARK_NAVY, borderRight: '1px solid #111', position: 'fixed', top: 0, zIndex: 1000, transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)', display: 'flex', flexDirection: 'column' };
const header = { padding: '40px 25px', borderBottom: '1px solid rgba(255,255,255,0.02)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const logoSection = { display: 'flex', flexDirection: 'column' };
const logoText = { color: GOLD, fontSize: '1.4rem', fontWeight: '900', letterSpacing: '4px', margin: 0 };
const tagline = { color: '#333', fontSize: '0.55rem', fontWeight: 'bold', letterSpacing: '2px', marginTop: '4px' };
const navLinks = { padding: '15px', flex: 1, overflowY: 'auto' };
const navLink = { display: 'flex', alignItems: 'center', padding: '14px 18px', textDecoration: 'none', fontSize: '0.7rem', fontWeight: 'bold', letterSpacing: '1.5px', borderRadius: '8px', marginBottom: '6px', transition: '0.3s' };
const iconSpan = { marginRight: '16px', fontSize: '1.1rem' };
const footer = { padding: '25px', borderTop: '1px solid rgba(255,255,255,0.02)', background: 'rgba(0,0,0,0.2)' };
const userBrief = { display: 'flex', alignItems: 'center', marginBottom: '20px' };
const avatar = { width: '40px', height: '40px', borderRadius: '12px', background: '#0a0a0a', color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.9rem', border: `1px solid ${GOLD}33` };
const userInfo = { marginLeft: '15px' };
const userName = { color: '#fff', fontSize: '0.8rem', fontWeight: 'bold', letterSpacing: '0.5px' };
const userRole = { color: '#444', fontSize: '0.6rem', fontWeight: 'bold', letterSpacing: '1px', marginTop: '2px' };
const logoutBtn = { width: '100%', padding: '12px', background: 'none', border: '1px solid #222', color: '#555', borderRadius: '10px', fontSize: '0.65rem', fontWeight: 'bold', cursor: 'pointer', letterSpacing: '1px' };
const mobileToggle = { position: 'fixed', top: '25px', right: '25px', zIndex: 1100, background: GOLD, border: 'none', width: '45px', height: '45px', borderRadius: '12px', color: '#000', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 10px 20px rgba(0,0,0,0.5)' };
const notifBtn = { background: 'none', border: 'none', color: GOLD, fontSize: '1.3rem', cursor: 'pointer', position: 'relative' };
const notifBadge = { position: 'absolute', top: '-5px', right: '-8px', background: '#ff4444', color: 'white', fontSize: '0.55rem', padding: '2px 6px', borderRadius: '20px', fontWeight: '900', border: `2px solid ${DARK_NAVY}` };
const notifPanel = { position: 'fixed', top: '25px', left: '275px', width: '320px', maxHeight: '85vh', background: '#080808', border: '1px solid #111', borderRadius: '24px', zIndex: 2000, display: 'flex', flexDirection: 'column', boxShadow: '0 30px 60px rgba(0,0,0,0.9)' };
const notifHeader = { padding: '20px', borderBottom: '1px solid #111', display: 'flex', justifyContent: 'space-between', color: GOLD, fontSize: '0.75rem', fontWeight: '900', letterSpacing: '2px' };
const notifList = { padding: '15px', overflowY: 'auto' };
const notifCard = { padding: '18px', background: '#0a0a0a', borderRadius: '15px', marginBottom: '12px', border: '1px solid #151515' };
const toastContainer = { position: 'fixed', bottom: '30px', right: '30px', width: '340px', background: '#050a0f', border: `1px solid ${GOLD}44`, borderRadius: '18px', zIndex: 3000, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.8)' };
const toastContent = { display: 'flex', alignItems: 'center', padding: '20px', gap: '15px' };
const toastIcon = { fontSize: '1.4rem' };
const toastLabel = { fontSize: '0.65rem', color: GOLD, fontWeight: '900', letterSpacing: '1px' };
const toastMsg = { margin: '4px 0 0', color: '#ccc', fontSize: '0.85rem', lineHeight: '1.4' };
const toastClose = { background: 'none', border: 'none', color: '#444', fontSize: '1.1rem', cursor: 'pointer' };
const toastProgress = { height: '4px', background: GOLD, width: '100%' };
const modalOverlayFixed = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.95)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '25px', backdropFilter: 'blur(10px)' };
const broadcastBox = { background: '#050a0f', border: `1px solid ${GOLD}22`, padding: '45px', borderRadius: '30px', width: '100%', maxWidth: '450px', textAlign: 'center', boxShadow: `0 0 60px ${GOLD}11` };
const darkTextarea = { width: '100%', height: '140px', background: '#000', border: '1px solid #111', borderRadius: '15px', padding: '20px', color: '#fff', fontSize: '0.9rem', marginBottom: '25px', outline: 'none', resize: 'none', fontFamily: 'inherit' };
const goldBtn = { width: '100%', padding: '16px', background: GOLD, border: 'none', borderRadius: '15px', color: '#000', fontWeight: '900', fontSize: '0.85rem', cursor: 'pointer', letterSpacing: '2px' };
const cancelLink = { display: 'block', marginTop: '20px', color: '#444', fontSize: '0.75rem', fontWeight: 'bold', textDecoration: 'none', background: 'none', border: 'none', width: '100%', cursor: 'pointer', letterSpacing: '1px' };
